<template>
  <section class="py-24 bg-white relative">
    <div class="max-w-[1400px] mx-auto px-6">
      <div class="text-center max-w-2xl mx-auto mb-16">
        <h2 class="text-4xl lg:text-5xl font-serif text-[#1c4848] font-semibold mb-4 tracking-tight">
          {{ t('home.features.title') }}
        </h2>
        <p class="text-[#1c4848]/60 font-medium">
          {{ t('home.features.subtitle') }}
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 auto-rows-[280px] mb-24">
        <div
          v-reveal
          class="lg:col-span-1 lg:row-span-2 bg-[#fafbfc] rounded-[40px] p-8 border border-[#1c4848]/5 relative overflow-hidden group hover:shadow-xl transition-all duration-500 flex flex-col"
        >
          <div
            class="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-white to-transparent z-10 pointer-events-none"
          ></div>
          <div class="relative z-20 mb-auto">
            <iconify-icon icon="solar:layers-linear" class="text-3xl text-[#ecd79f] mb-4"></iconify-icon>
            <h3 class="text-2xl font-serif text-[#1c4848] font-semibold mb-2 tracking-tight">
              {{ t('home.features.core.dynamicFeed.title') }}
            </h3>
            <p class="text-sm text-[#1c4848]/60 font-medium">
              {{ t('home.features.core.dynamicFeed.body') }}
            </p>
          </div>

          <div
            class="relative w-full flex flex-col gap-3 translate-y-8 group-hover:translate-y-2 transition-transform duration-700 ease-out mt-6"
          >
            <div
              class="bg-white p-4 rounded-2xl shadow-sm border border-[#1c4848]/5 flex items-center gap-3"
            >
              <div
                class="w-10 h-10 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center shrink-0"
              >
                <iconify-icon icon="solar:book-linear" class="text-xl"></iconify-icon>
              </div>
              <div class="min-w-0">
                <span
                  class="text-[10px] font-semibold text-[#1c4848]/40 uppercase tracking-widest block"
                >
                  {{ t('home.features.core.dynamicFeed.bookAddedLabel') }}
                </span>
                <p class="text-sm font-semibold text-[#1c4848] truncate">
                  {{ t('home.features.core.dynamicFeed.bookAddedValue') }}
                </p>
              </div>
            </div>

            <div
              class="bg-white p-4 rounded-2xl shadow-sm border border-[#1c4848]/5 flex items-center gap-3 opacity-70 scale-[0.98]"
            >
              <div
                class="w-10 h-10 rounded-lg bg-[#ecd79f]/20 text-[#1c4848] flex items-center justify-center shrink-0"
              >
                <iconify-icon icon="solar:microphone-3-linear" class="text-xl"></iconify-icon>
              </div>
              <div class="min-w-0">
                <span
                  class="text-[10px] font-semibold text-[#1c4848]/40 uppercase tracking-widest block"
                >
                  {{ t('home.features.core.dynamicFeed.podcastLabel') }}
                </span>
                <p class="text-sm font-semibold text-[#1c4848] truncate">
                  {{ t('home.features.core.dynamicFeed.podcastValue') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          v-reveal="{ delayMs: 80 }"
          class="md:col-span-2 lg:col-span-2 bg-[#1c4848] rounded-[40px] p-8 relative overflow-hidden group hover:shadow-xl transition-all duration-500 flex flex-col justify-between"
        >
          <div
            class="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay pointer-events-none"
          ></div>
          <svg
            class="absolute right-0 bottom-0 w-2/3 h-full text-[#ecd79f]/10"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <path
              d="M 10,100 C 40,80 50,30 90,10"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-dasharray="4"
            ></path>
          </svg>

          <div class="relative z-10">
            <div class="flex items-center justify-between mb-2">
              <iconify-icon
                icon="solar:routing-2-linear"
                class="text-3xl text-[#ecd79f]"
              ></iconify-icon>
              <span
                class="text-[10px] font-semibold text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-md tracking-widest uppercase border border-emerald-400/20"
              >
                {{ t('home.features.core.mosqueNavigator.badge') }}
              </span>
            </div>
            <h3 class="text-2xl font-serif text-white font-semibold mb-2 tracking-tight">
              {{ t('home.features.core.mosqueNavigator.title') }}
            </h3>
            <p class="text-sm text-white/60 font-medium max-w-sm">
              {{ t('home.features.core.mosqueNavigator.body') }}
            </p>
          </div>

          <div
            class="relative z-10 flex items-center gap-4 bg-white/10 backdrop-blur-md rounded-2xl p-4 w-fit border border-white/10 mt-6 group-hover:-translate-y-1 transition-transform"
          >
            <div
              class="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center animate-pulse-ring"
            >
              <iconify-icon icon="solar:map-point-bold" class="text-xl"></iconify-icon>
            </div>
            <div>
              <p class="text-white font-semibold text-sm">
                {{ t('home.features.core.mosqueNavigator.cardTitle') }}
              </p>
              <p class="text-emerald-400 text-xs font-semibold mt-0.5">
                {{ t('home.features.core.mosqueNavigator.cardMeta') }}
              </p>
            </div>
            <button
              type="button"
              class="ml-4 w-8 h-8 rounded-full bg-white text-[#1c4848] flex items-center justify-center hover:scale-105 transition-transform"
            >
              <iconify-icon icon="solar:arrow-right-up-linear"></iconify-icon>
            </button>
          </div>
        </div>

        <div
          v-reveal="{ delayMs: 140 }"
          class="bg-indigo-50 rounded-[40px] p-8 border border-indigo-100 relative overflow-hidden group hover:shadow-xl transition-all duration-500 flex flex-col justify-between"
        >
          <div
            class="absolute -right-6 -top-6 w-32 h-32 bg-indigo-200/50 rounded-full blur-3xl group-hover:bg-indigo-300/50 transition-colors"
          ></div>
          <div>
            <iconify-icon
              icon="solar:moon-sleep-linear"
              class="text-3xl text-indigo-600 mb-4"
            ></iconify-icon>
            <h3 class="text-2xl font-serif text-[#1c4848] font-semibold mb-2 tracking-tight">
              {{ t('home.features.core.dreamInterpreter.title') }}
            </h3>
            <p class="text-sm text-[#1c4848]/60 font-medium">
              {{ t('home.features.core.dreamInterpreter.body') }}
            </p>
          </div>
          <div class="flex items-center gap-2 mt-4 text-indigo-600 font-semibold text-xs tracking-wide uppercase">
            <iconify-icon icon="solar:magic-stick-3-bold"></iconify-icon>
            {{ t('home.features.core.dreamInterpreter.cta') }}
          </div>
        </div>

        <div
          v-reveal="{ delayMs: 200 }"
          class="md:col-span-3 lg:col-span-3 bg-[#ecd79f] rounded-[40px] p-8 relative overflow-hidden group hover:shadow-xl transition-all duration-500 flex flex-col justify-between"
        >
          <div
            class="absolute top-0 right-0 w-48 h-48 bg-white/30 rounded-bl-full transform origin-top-right group-hover:scale-110 transition-transform duration-500"
          ></div>

          <div class="relative z-10 flex flex-col md:flex-row gap-8 items-center justify-between h-full">
            <div class="max-w-md">
              <iconify-icon
                icon="solar:book-bookmark-minimalistic-linear"
                class="text-3xl text-[#1c4848] mb-4"
              ></iconify-icon>
              <h3 class="text-2xl font-serif text-[#1c4848] font-semibold mb-3 tracking-tight">
                {{ t('home.features.core.libraryStats.title') }}
              </h3>
              <p class="text-sm text-[#1c4848]/70 font-medium leading-relaxed mb-4">
                {{ t('home.features.core.libraryStats.body') }}
              </p>
              <div class="flex flex-wrap items-center gap-3 text-xs font-semibold text-[#1c4848]/60">
                <span class="bg-white/40 px-2 py-1 rounded-md">
                  {{ t('home.features.core.libraryStats.chips.pages') }}
                </span>
                <span class="bg-white/40 px-2 py-1 rounded-md">
                  {{ t('home.features.core.libraryStats.chips.bookmarks') }}
                </span>
                <span class="bg-white/40 px-2 py-1 rounded-md">
                  {{ t('home.features.core.libraryStats.chips.tajweedCheck') }}
                </span>
              </div>
            </div>

            <div class="flex flex-col gap-4 w-full max-w-[260px] shrink-0">
              <div
                class="w-full bg-white p-5 rounded-3xl shadow-sm border border-white/50 group-hover:-translate-y-1 transition-transform"
              >
                <div class="flex justify-between items-center mb-4">
                  <span class="text-xs font-semibold text-[#1c4848]/50 uppercase tracking-widest">
                    {{ t('home.features.core.libraryStats.paceLabel') }}
                  </span>
                  <span class="text-sm font-semibold text-[#1c4848] bg-[#ecd79f]/20 px-2 py-0.5 rounded-md">
                    {{ t('home.features.core.libraryStats.paceValue') }}
                  </span>
                </div>
                <div class="relative h-2 w-full bg-[#1c4848]/10 rounded-full mb-4">
                  <div class="absolute top-0 left-0 h-full w-[40%] bg-[#1c4848] rounded-full"></div>
                  <div
                    class="absolute top-1/2 left-[40%] -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-white border-2 border-[#1c4848] rounded-full shadow-md cursor-pointer hover:scale-110 transition-transform"
                  ></div>
                </div>
                <div class="text-center bg-[#fafbfc] rounded-xl py-2 border border-[#1c4848]/5">
                  <p class="text-[10px] font-semibold text-[#1c4848]/50 uppercase">
                    {{ t('home.features.core.libraryStats.finishLabel') }}
                  </p>
                  <p class="text-lg font-serif text-[#1c4848] font-semibold">
                    {{ t('home.features.core.libraryStats.finishValue') }}
                  </p>
                </div>
              </div>

              <div
                class="w-full bg-white p-4 rounded-3xl shadow-sm border border-emerald-100 flex items-center justify-between group-hover:-translate-y-1 transition-transform delay-75 relative overflow-hidden"
              >
                <div
                  class="absolute inset-0 bg-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity"
                ></div>
                <div>
                  <div class="flex items-center gap-1 mb-1">
                    <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span class="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">
                      {{ t('home.features.core.libraryStats.longPressLabel') }}
                    </span>
                  </div>
                  <p class="text-sm font-serif text-[#1c4848] font-semibold">
                    {{ t('home.features.core.libraryStats.longPressValue') }}
                  </p>
                </div>
                <div
                  class="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shadow-inner"
                >
                  <iconify-icon icon="solar:microphone-2-bold" class="text-xl"></iconify-icon>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <HomePremiumTools />
    </div>
  </section>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import HomePremiumTools from '@/components/home/HomePremiumTools.vue';

const { t } = useI18n();
</script>

